import { IconClose } from "@assets/icons";
import BrandLogo from "@components/data-display/BrandLogo";
import { IframeDialogCloseReason } from "@configs/statsig";
import useTypedSelector from "@hooks/useTypedSelector";
import logger from "@utils/logger";
import { Dialog, Prompt, cn, delay } from "kz-ui-sdk";
import NoSleep from "nosleep.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { OpenIframeDialogOptions, hideDownloadBanner } from ".";

export const OpenIframeDialog = "OPEN_IFRAME_DIALOG";
export const CloseIframeDialog = "CLOSE_IFRAME_DIALOG";
export const IframeDialogClosed = "IFRAME_DIALOG_CLOSED";
export const UpdateIframeUrl = "UPDATE_IFRAME_URL";
export const UpdateIframeSrcDoc = "UPDATE_IFRAME_SRC_DOC";
export const HideBannerWhenOpenGame = "HIDE_BANNER";

export interface CustomData {
  url?: string | null;
  title: string | React.ReactNode;
  uniqueId?: string;
  options?: OpenIframeDialogOptions;
}

export interface CustomCloseData {
  uniqueId?: string;
  reason?: string;
}

export interface CustomClosedData {
  uniqueId?: string;
  reason?: string;
  options?: OpenIframeDialogOptions;
}

export interface CustomUpdateUrlData {
  url?: string;
  html?: string;
}
const noSleep = new NoSleep();

type HistoryState = ReturnType<typeof window.history.state>;

// Delay before starting history restoration after dialog close
const HISTORY_RESTORATION_INITIAL_DELAY_MS = 500;

// Delay between history navigation steps to allow state to update
const HISTORY_RESTORATION_STEP_DELAY_MS = 50;

// Maximum number of history steps to attempt when restoring original state
const HISTORY_RESTORATION_MAX_STEPS = 10;

export default function IframeDialog() {
  const [iframeUrl, setIframeUrl] = useState<string | undefined | null>(undefined);
  const [iframeSrcDoc, setIframeSrcDoc] = useState<string>();
  const [iframeTitle, setIframeTitle] = useState<string | React.ReactNode>("");
  const [iframeOptions, setIframeOptions] = useState<OpenIframeDialogOptions | undefined>(undefined);
  const [isOpenConfirmCloseDrawer, setIsOpenConfirmCloseDrawer] = useState(false);
  const [id, setId] = useState<string | undefined>(undefined);
  const originalStateRef = useRef<HistoryState | null>(null);
  const { t } = useTranslation();
  const isDisplayAppInstaller = useTypedSelector((state) => state.preferences.isDisplayAppInstaller);

  const closeDialog = useCallback(
    (options?: { popHistory?: boolean; reason?: string }) => {
      const { popHistory = true, reason } = options ?? {};
      const currentId = id;
      const currentOptions = iframeOptions;

      setIframeUrl(undefined);
      setIframeSrcDoc(undefined);
      setIframeTitle("");
      setId(undefined);

      hideDownloadBanner(false);

      // Dispatch notification event that dialog has closed
      window.dispatchEvent(
        new CustomEvent(IframeDialogClosed, {
          detail: {
            uniqueId: currentId,
            reason,
            options: currentOptions,
          },
        }),
      );
      // if user close by normal close button on header, we should remove the history
      if (popHistory && iframeOptions?.closeOnBack) {
        // Capture the original state before resetting it
        const capturedOriginalState = originalStateRef.current;
        originalStateRef.current = null;
        delay(HISTORY_RESTORATION_INITIAL_DELAY_MS).then(async () => {
          if (capturedOriginalState !== null) {
            // Helper function to check if two states match
            const statesMatch = (state1: HistoryState, state2: HistoryState): boolean => {
              if (state1 === state2) return true;
              if (!state1 || !state2) return false;
              // Deep comparison of state objects
              try {
                return JSON.stringify(state1) === JSON.stringify(state2);
              } catch (error) {
                // If serialization fails (circular reference, etc.), fall back to shallow comparison
                logger._console.warn("[IframeDialog] State comparison failed, using shallow comparison", error);
                return state1 === state2;
              }
            };

            const currentState = window.history.state;

            logger._console.debug("[IframeDialog] Starting history restoration", {
              originalState: capturedOriginalState,
              currentState,
              reason,
            });

            // Check if we're already at the original state
            if (statesMatch(currentState, capturedOriginalState)) {
              logger._console.debug("[IframeDialog] Already at original state, no navigation needed");
              return;
            }

            // Go back step by step until we match the original state
            const goBackUntilMatch = async (step: number): Promise<void> => {
              if (step > HISTORY_RESTORATION_MAX_STEPS) {
                logger._console.debug("[IframeDialog] Reached max steps, stopping history restoration", {
                  stepsAttempted: step - 1,
                });
                return;
              }

              window.history.go(-1);
              // Use delay utility to allow history to update before checking state
              await delay(HISTORY_RESTORATION_STEP_DELAY_MS);

              const newState = window.history.state;
              logger._console.debug("[IframeDialog] Checking history state after going back", {
                step,
                newState,
                originalState: capturedOriginalState,
                matches: statesMatch(newState, capturedOriginalState),
              });

              if (statesMatch(newState, capturedOriginalState)) {
                logger._console.debug("[IframeDialog] Successfully restored to original state", {
                  stepsBack: step,
                });
                return;
              }

              if (step < HISTORY_RESTORATION_MAX_STEPS) {
                return goBackUntilMatch(step + 1);
              } else {
                logger._console.debug("[IframeDialog] Reached max steps without matching original state", {
                  stepsAttempted: step,
                  finalState: newState,
                });
              }
            };

            await goBackUntilMatch(1);
          } else {
            // Fallback to single back if original state is not set
            logger._console.debug(
              "[IframeDialog] History restoration fallback: original state not set, using history.back()",
              {
                reason,
              },
            );
            window.history.back();
          }
        });
      } else {
        originalStateRef.current = null;
      }
      iframeOptions?.onClose?.();
      // Generic tracking callback
      iframeOptions?.onTrackClose?.({ id: currentId, reason });
    },
    [iframeOptions, id],
  );

  const handleStay = useCallback((e?: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsOpenConfirmCloseDrawer(false);
  }, []);

  const handleExit = useCallback(
    (e?: any) => {
      e?.preventDefault();
      e?.stopPropagation();
      setIsOpenConfirmCloseDrawer(false);
      closeDialog({ reason: IframeDialogCloseReason.MANUAL });
    },
    [closeDialog],
  );

  // handle popstate event (back button) to close iframe without navigating back
  useEffect(() => {
    if (!iframeOptions?.closeOnBack) return;
    const handlePopState = (event: PopStateEvent) => {
      if (!!iframeUrl && iframeOptions?.closeOnBack) {
        closeDialog({
          popHistory: false,
          reason: IframeDialogCloseReason.GO_BACK_HISTORY,
        });
        event.preventDefault();
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [closeDialog, iframeUrl, iframeOptions?.closeOnBack]);

  useEffect(() => {
    const handleRemoteOpen = (e: CustomEvent<CustomData>) => {
      setIframeUrl(e.detail.url);
      setIframeTitle(e.detail.title);
      const options = {
        withCloseButton: false,
        withHeader: true,
        withConfirmCloseDialog: false,
        withLogo: false,
        withAppInstallerOffset: true,
        ...e.detail.options,
      };
      setIframeOptions(options);
      setId(e.detail.uniqueId);

      // Push history state when dialog opens with closeOnBack
      if (options.closeOnBack) {
        // Store the original state before pushing our state
        originalStateRef.current = window.history.state;
        logger._console.debug("[IframeDialog] Opening dialog with closeOnBack", {
          originalState: originalStateRef.current,
          uniqueId: e.detail.uniqueId,
        });
        window.history.pushState({ iframeDialog: true }, document.title, window.location.href);
        logger._console.debug("[IframeDialog] History state after push", {
          currentState: window.history.state,
          originalState: originalStateRef.current,
        });
      } else {
        // Reset originalState if closeOnBack is false
        originalStateRef.current = null;
      }
    };
    const handleRemoteClose = (e: CustomEvent<CustomCloseData>) => {
      if (e.detail.uniqueId && e.detail.uniqueId !== id) return;
      noSleep.disable();
      closeDialog({ reason: e.detail?.reason as string | undefined });
    };
    const handleUpdateIframeUrl = (e: CustomEvent<CustomUpdateUrlData>) => {
      if (e.detail.url) {
        setIframeUrl(e.detail.url);
      }
    };
    const handleUpdateIframeSrcDoc = (e: CustomEvent<CustomUpdateUrlData>) => {
      if (e.detail.html) {
        setIframeSrcDoc(e.detail.html);
      }
    };
    //
    window.addEventListener(OpenIframeDialog, handleRemoteOpen as EventListener);
    window.addEventListener(CloseIframeDialog, handleRemoteClose as EventListener);
    window.addEventListener(UpdateIframeUrl, handleUpdateIframeUrl as EventListener);
    window.addEventListener(UpdateIframeSrcDoc, handleUpdateIframeSrcDoc as EventListener);

    return () => {
      window.removeEventListener(OpenIframeDialog, handleRemoteOpen as EventListener);
      window.removeEventListener(CloseIframeDialog, handleRemoteClose as EventListener);
      window.removeEventListener(UpdateIframeUrl, handleUpdateIframeUrl as EventListener);
      window.removeEventListener(UpdateIframeSrcDoc, handleUpdateIframeSrcDoc as EventListener);
    };
  }, [closeDialog, id]);

  const withAppInstallerOffset = useMemo(() => {
    return isDisplayAppInstaller && iframeOptions?.withAppInstallerOffset;
  }, [isDisplayAppInstaller, iframeOptions?.withAppInstallerOffset]);

  const isOpen = iframeUrl !== undefined || !!iframeSrcDoc;
  const doStayOnConfirm = iframeOptions?.confirmDialogConfig?.stayOnConfirm === true;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => {}}
        title={
          <div className="flex flex-col items-center justify-center">
            <BrandLogo className="opacity-50" />
          </div>
        }
        classes={{
          "backDrop&": "!pointer-events-none",
          "container&": "bg-none border-0 shadow-none",
          "containerWrapper&": cn("max-w-md left-1/2 -translate-x-1/2", {
            "top-10 h-[calc(100dvh-40px)]": iframeOptions?.withHeader && withAppInstallerOffset,
            "landscape:max-w-full landscape:left-0 landscape:translate-x-0": iframeOptions?.allowLandscapeMode,
            "z-[10000]": !iframeOptions?.withAppInstallerOffset,
          }),
        }}
        className="z-[1030]"
        transition
        zIndex={20}
      >
        {iframeOptions?.withHeader && (
          <div className={cn("absolute left-0 top-0 z-[100] flex h-8 w-full items-center bg-dialog px-1 py-1")}>
            {iframeOptions?.withLogo && (
              <BrandLogo
                size="auto"
                className="py-1"
              />
            )}
            <span className="ml-1 line-clamp-1">{iframeTitle}</span>
            <button
              className="ml-auto mr-1 capitalize underline"
              onClick={() => {
                if (iframeOptions.withConfirmCloseDialog) {
                  setIsOpenConfirmCloseDrawer(true);
                } else {
                  closeDialog({ reason: IframeDialogCloseReason.MANUAL });
                }
              }}
              aria-label={`Close iframe dialog`}
            >
              {t("close")}
            </button>
          </div>
        )}
        <div
          className={cn("fixed left-0 top-0 z-[100] h-full w-full border-0", {
            "!top-8": iframeOptions?.withHeader,
            "!h-[calc(100dvh-32px)]": !withAppInstallerOffset,
            "!h-[calc(100dvh-72px)]": withAppInstallerOffset,
            "pointer-events-none": isOpenConfirmCloseDrawer,
          })}
        >
          {(iframeUrl || iframeSrcDoc) && (
            <iframe
              src={iframeUrl ?? undefined}
              srcDoc={iframeSrcDoc}
              className="h-full w-full"
              allow="clipboard-read; clipboard-write"
              data-is-game={iframeOptions?.isGame === true ? "true" : undefined}
            />
          )}
        </div>
      </Dialog>
      {iframeOptions?.withCloseButton && (
        <IconClose
          width={32}
          height={32}
          onClick={(e) => {
            e.preventDefault();
            closeDialog({ reason: IframeDialogCloseReason.MANUAL });
          }}
        />
      )}

      <Prompt
        open={isOpenConfirmCloseDrawer}
        onClose={() => setIsOpenConfirmCloseDrawer(false)}
        title={t(iframeOptions?.confirmDialogConfig?.title || "confirm")}
        content={t(iframeOptions?.closeConfirmMessage as string)}
        onConfirm={doStayOnConfirm ? handleStay : handleExit}
        onCancel={doStayOnConfirm ? handleExit : handleStay}
        messages={{
          confirm: t(doStayOnConfirm ? "stay" : "confirm"),
          cancel: t(doStayOnConfirm ? "exit" : "close"),
        }}
        zIndex={20}
      />
    </>
  );
}
